import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { understandingTopics } from "../lib/content";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import LastReviewed from "../components/LastReviewed";
import { Link } from "react-router-dom";

export default function UnderstandingPage() {
  const { hash } = useLocation();
  const [openTopic, setOpenTopic] = useState("");

  useEffect(() => {
    const id = hash.replace(/^#/, "");
    if (!id) return;
    if (understandingTopics.some((t) => t.id === id)) {
      setOpenTopic(id);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="topic-${id}-trigger"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [hash]);

  return (
    <div data-testid="understanding-page">
      <PageMeta
        title="Understanding the system"
        description="Plain-English explanations of child protection conferences, court orders, your rights, and what to expect at each stage of the system in England and Wales."
        path="/understanding"
      />
      <PageHeader
        overline="Understanding the system"
        title="The process, in plain English."
        lede="What each stage actually means. What you can expect. What you are entitled to ask for, see, and challenge."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <aside className="lg:col-span-3 lg:sticky lg:top-28 lg:self-start">
            <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
              Six explainers
            </div>
            <p className="mt-4 text-ink-muted leading-relaxed text-sm">
              Tap any heading to read. We&rsquo;ve kept each one short and direct, and listed practical things you can do at the end.
            </p>
            <div className="mt-6 h-px w-12 bg-ember/60" />
            <p className="mt-6 text-xs text-ink-muted font-accent">
              This is general information, not legal advice. For your case, please speak to a solicitor. Care proceedings legal aid is free for parents.
            </p>
            <LastReviewed path="/understanding" className="mt-6" />
          </aside>

          <div className="lg:col-span-9">
            <Accordion
              type="single"
              collapsible
              value={openTopic}
              onValueChange={setOpenTopic}
              className="w-full divide-y divide-rule border-y border-rule"
              data-testid="understanding-accordion"
            >
              {understandingTopics.map((topic) => (
                <AccordionItem
                  key={topic.id}
                  value={topic.id}
                  className="border-b-0"
                >
                  <AccordionTrigger
                    data-testid={`topic-${topic.id}-trigger`}
                    className="text-left hover:no-underline py-7 group"
                  >
                    <div className="flex-1 pr-8">
                      <div className="font-serif text-2xl md:text-[1.7rem] text-ink tracking-tight leading-tight group-hover:text-river transition-colors">
                        {topic.title}
                      </div>
                      <div className="mt-2 text-ink-muted text-sm md:text-base font-light leading-relaxed">
                        {topic.summary}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-2">
                    <div className="space-y-5 text-base md:text-lg text-ink-muted leading-[1.75] max-w-3xl font-light">
                      {topic.body.map((para) => (
                        <p key={`${topic.id}-${para.slice(0, 40)}`}>{para}</p>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-16 border border-rule p-8 md:p-10 bg-linen-alt">
              <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
                Also in this section
              </div>
              <h3 className="font-serif text-2xl md:text-3xl mt-4 text-ink leading-tight tracking-tight">
                Getting your records.
              </h3>
              <p className="mt-4 text-ink-muted leading-relaxed max-w-2xl">
                How to ask a local authority or Cafcass for the information they hold about you, what they can lawfully hold back, why &ldquo;it&rsquo;s legally privileged&rdquo; is often wrong, and how to complain to the Information Commissioner.
              </p>
              <Link
                to="/understanding/records"
                data-testid="understanding-records-link"
                className="mt-6 inline-flex items-center gap-2 border border-ink/30 text-ink px-6 py-3 text-sm font-accent font-semibold tracking-wide hover:border-ink hover:bg-white transition-colors"
              >
                Read the guide
              </Link>
            </div>

            <div className="mt-8 border border-rule p-8 md:p-10 bg-linen-alt">
              <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
                Take it with you
              </div>
              <h3 className="font-serif text-2xl md:text-3xl mt-4 text-ink leading-tight tracking-tight">
                Before your next meeting.
              </h3>
              <p className="mt-4 text-ink-muted leading-relaxed max-w-2xl">
                A printable one-page checklist for child protection conferences, core groups and child in need meetings: what to bring, what to ask, and what to write down the same day.
              </p>
              <Link
                to="/understanding/meeting-prep"
                data-testid="understanding-meeting-prep-link"
                className="mt-6 inline-flex items-center gap-2 border border-ink/30 text-ink px-6 py-3 text-sm font-accent font-semibold tracking-wide hover:border-ink hover:bg-white transition-colors"
              >
                Get the checklist
              </Link>
            </div>

            <div className="mt-8 border border-rule p-8 md:p-10 bg-linen-alt">
              <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
                If you only do one thing today
              </div>
              <h3 className="font-serif text-2xl md:text-3xl mt-4 text-ink leading-tight tracking-tight">
                Get a solicitor on your side.
              </h3>
              <p className="mt-4 text-ink-muted leading-relaxed max-w-2xl">
                In care proceedings, legal aid for parents is free and non-means-tested. You should not face this without representation. Our support page lists trusted starting points.
              </p>
              <Link
                to="/support"
                data-testid="understanding-cta-support-link"
                className="mt-6 inline-flex items-center gap-2 bg-river text-linen px-6 py-3 text-sm font-accent font-semibold tracking-wide hover:bg-river-dark transition-colors"
              >
                See support directory
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
